import { toaster } from "@/components/ui/toaster"
import { Tooltip } from "@/components/ui/tooltip"
import { useSuggestTopics } from "@/hooks/useSuggestTopics"
import { Button, IconButton } from "@chakra-ui/react"
import { useState } from "react"
import { FiZap } from "react-icons/fi"
import { TopicSuggestionsModal } from "./TopicSuggestionsModal"

interface SuggestTopicsButtonProps {
  questionText: string
  options: string[]
  questionId?: string
  subjectIds?: string[]
  onTopicsSuggested: (topics: string[]) => void
  variant?: "button" | "icon"
  disabled?: boolean
}

export function SuggestTopicsButton({
  questionText,
  options,
  questionId,
  subjectIds,
  onTopicsSuggested,
  variant = "button",
  disabled = false,
}: SuggestTopicsButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [suggestedTopics, setSuggestedTopics] = useState<{
    topics: string[]
    provider: string
    model: string
    cost?: number
  }>()

  const suggestMutation = useSuggestTopics()

  const handleSuggest = () => {
    // Validate inputs
    if (!questionText.trim()) {
      toaster.create({
        title: "Question text is required",
        description: "Please enter a question before suggesting topics.",
        type: "error",
      })
      return
    }

    if (!options || options.length < 2) {
      toaster.create({
        title: "Options required",
        description:
          "Please add at least 2 answer options before suggesting topics.",
        type: "error",
      })
      return
    }

    // Check if all options are populated
    const emptyOptions = options.filter((opt) => !opt || !opt.trim())
    if (emptyOptions.length > 0) {
      toaster.create({
        title: "All options must be filled",
        description:
          "Please fill in all answer options before suggesting topics.",
        type: "error",
      })
      return
    }

    // Call API
    const params = {
      questionText,
      options,
      questionId,
      subjectIds,
    }

    suggestMutation.mutate(params, {
      onSuccess: (data) => {
        setSuggestedTopics({
          topics: data.suggested_topics,
          provider: data.provider,
          model: data.model,
          cost: data.estimated_cost ?? undefined,
        })
        setIsModalOpen(true)
      },
      onError: (error: Error) => {
        toaster.create({
          title: "Failed to suggest topics",
          description: error.message || "An error occurred. Please try again.",
          type: "error",
          action: {
            label: "Retry",
            onClick: handleSuggest,
          },
        })
      },
    })
  }

  const handleAccept = (topics: string[]) => {
    // Pass topic names to parent - parent will handle creation
    onTopicsSuggested(topics)
    setIsModalOpen(false)
  }

  const handleRegenerate = () => {
    setIsModalOpen(false)
    // Small delay before regenerating to allow modal to close
    setTimeout(handleSuggest, 100)
  }

  if (variant === "icon") {
    return (
      <>
        <Tooltip
          content="AI suggest relevant topics from your organization's topic list"
          positioning={{ placement: "top" }}
        >
          <IconButton
            aria-label="Suggest topics with AI"
            onClick={handleSuggest}
            disabled={disabled || suggestMutation.isPending}
            loading={suggestMutation.isPending}
            variant="outline"
            size="sm"
          >
            <FiZap />
          </IconButton>
        </Tooltip>

        {suggestedTopics && (
          <TopicSuggestionsModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            suggestedTopics={suggestedTopics.topics}
            isLoading={false}
            onAccept={handleAccept}
            onRegenerate={handleRegenerate}
            provider={suggestedTopics.provider}
            model={suggestedTopics.model}
            estimatedCost={suggestedTopics.cost}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Button
        onClick={handleSuggest}
        disabled={disabled || suggestMutation.isPending}
        loading={suggestMutation.isPending}
        variant="outline"
        size="sm"
        gap={2}
      >
        <FiZap />
        Suggest Topics with AI
      </Button>

      {suggestedTopics && (
        <TopicSuggestionsModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          suggestedTopics={suggestedTopics.topics}
          isLoading={false}
          onAccept={handleAccept}
          onRegenerate={handleRegenerate}
          provider={suggestedTopics.provider}
          model={suggestedTopics.model}
          estimatedCost={suggestedTopics.cost}
        />
      )}
    </>
  )
}
